"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.preparePastedEmailHtml = exports.normalizePastedEmailSource = exports.trimToEmailMarkup = exports.isCanvaEmbedPaste = exports.hasVisibleEmailContent = exports.isMissingHtmlMarkup = void 0;
const emails_1 = require("../constants/emails");
const email_1 = require("./email");
/**
 * Detects a paste that is not HTML at all — most often a link to the email instead of its source.
 *
 * Matching link shapes (`https://`, `www.`, `view-source:`, bare paths, bare domains) is a losing game, so
 * we invert the test: every real email HTML paste opens at least one element, and no URL ever does. `test`
 * short-circuits on the first tag, so this stays cheap on the 300k-char budget even on every keystroke.
 */
const isMissingHtmlMarkup = (input) => {
    const trimmed = input.trim();
    return trimmed.length > 0 && !/<[a-z][^<>]*>/i.test(trimmed);
};
exports.isMissingHtmlMarkup = isMissingHtmlMarkup;
const CONDITIONAL_COMMENT_OPEN_RE = /<!--\[if[^\]>]{0,200}\]>(?:<!-->)?/gi;
const CONDITIONAL_COMMENT_CLOSE_RE = /(?:<!--)?<!\[endif\]-->/gi;
const INVISIBLE_ELEMENTS = ["head", "title", "style", "script", "iframe", "object"];
const TAG_NAME_CHARACTER_RE = /[a-z0-9:-]/i;
const MARKUP_START_RE = /^<[a-z/!?]/i;
const VISIBLE_TAG_RE = /^<(?:img|picture|video|svg|hr|v:image|v:fill)\b|^<input\b[\s\S]*\btype\s*=\s*["']?image\b/i;
const BACKGROUND_IMAGE_RE = /\sbackground\s*=\s*["']?[^"'\s>]|background(?:-image)?\s*:[^;"'>]{0,200}url\(/i;
const INVISIBLE_TEXT_RE = /&(?:nbsp|zwnj|zwj|shy|#160|#xa0|#173|#xad|#847|#x34f|#8203|#x200b|#8204|#x200c|#8205|#x200d|#8288|#x2060|#65279|#xfeff);|[\s\u00a0\u00ad\u034f\u200b-\u200d\u2060\ufeff]/gi;
const removeDelimited = (html, opening, closing, isOpeningMatch) => {
    const lower = html.toLowerCase();
    const kept = [];
    let cursor = 0;
    let openIndex = lower.indexOf(opening);
    while (openIndex >= 0) {
        if (!isOpeningMatch(openIndex)) {
            openIndex = lower.indexOf(opening, openIndex + opening.length);
            continue;
        }
        const closeIndex = lower.indexOf(closing, openIndex + opening.length);
        const closeEnd = closeIndex < 0 ? -1 : lower.indexOf(">", closeIndex);
        if (closeEnd < 0) {
            break;
        }
        kept.push(html.slice(cursor, openIndex));
        cursor = closeEnd + 1;
        openIndex = lower.indexOf(opening, cursor);
    }
    kept.push(html.slice(cursor));
    return kept.join("");
};
const removeInvisibleMarkup = (html) => INVISIBLE_ELEMENTS.reduce((current, tagName) => removeDelimited(current, `<${tagName}`, `</${tagName}`, (index) => !TAG_NAME_CHARACTER_RE.test(current.charAt(index + tagName.length + 1))), removeDelimited(html.replace(CONDITIONAL_COMMENT_OPEN_RE, "").replace(CONDITIONAL_COMMENT_CLOSE_RE, ""), "<!--", "-->", () => true));
const hasVisibleText = (text) => text.replace(INVISIBLE_TEXT_RE, "").length > 0;
const hasVisibleEmailContent = (html) => {
    const body = removeInvisibleMarkup(html);
    let cursor = 0;
    while (cursor < body.length) {
        const tagStart = body.indexOf("<", cursor);
        if (hasVisibleText(body.slice(cursor, tagStart < 0 ? body.length : tagStart))) {
            return true;
        }
        if (tagStart < 0) {
            return false;
        }
        const tagEnd = body.indexOf(">", tagStart);
        const tag = body.slice(tagStart, tagEnd < 0 ? body.length : tagEnd + 1);
        if (!MARKUP_START_RE.test(tag) || VISIBLE_TAG_RE.test(tag) || BACKGROUND_IMAGE_RE.test(tag)) {
            return true;
        }
        if (tagEnd < 0) {
            return false;
        }
        cursor = tagEnd + 1;
    }
    return false;
};
exports.hasVisibleEmailContent = hasVisibleEmailContent;
const CANVA_EMBED_MAX_CHARS = 5_000;
const CANVA_EMBED_IFRAME_RE = /<iframe\b[^<>]*canva\.com\/design\/[^<>]*embed[^<>]*>/i;
const CANVA_DESIGN_LINK_WITH_ATTRIBUTION_RE = /<a\b[^<>]*canva\.com(?:\/|&#x2f;|&#47;|%2f)design[^<>]*>[^<]*<\/a>[^<]{0,200}/gi;
const isCanvaEmbedPaste = (html) => html.length <= CANVA_EMBED_MAX_CHARS &&
    CANVA_EMBED_IFRAME_RE.test(html) &&
    !(0, exports.hasVisibleEmailContent)(html.replace(CANVA_DESIGN_LINK_WITH_ATTRIBUTION_RE, ""));
exports.isCanvaEmbedPaste = isCanvaEmbedPaste;
const HTML_DOCUMENT_OPEN_RE = /<!doctype\s+html\b|<html\b/i;
const FIRST_TAG_RE = /<(?:[a-z]|!--|!doctype)/i;
const findMarkupBounds = (text) => {
    const documentStart = text.search(HTML_DOCUMENT_OPEN_RE);
    const lastCloseStart = text.toLowerCase().lastIndexOf("</html");
    const documentEnd = lastCloseStart >= 0 ? text.indexOf(">", lastCloseStart) + 1 : -1;
    if (documentStart >= 0 && documentEnd > documentStart) {
        return { start: documentStart, end: documentEnd, isDocument: true };
    }
    const fragmentStart = text.search(FIRST_TAG_RE);
    const fragmentEnd = text.lastIndexOf(">") + 1;
    if (fragmentStart < 0 || fragmentEnd <= fragmentStart) {
        return null;
    }
    return { start: fragmentStart, end: fragmentEnd, isDocument: false };
};
const isSurroundingProse = (text, isDocument) => text.trim().length > 0 && !FIRST_TAG_RE.test(text) && (isDocument || text.includes("\n"));
const trimToEmailMarkup = (text) => {
    const bounds = findMarkupBounds(text);
    if (!bounds) {
        return { html: text, isTrimmed: false };
    }
    const leading = text.slice(0, bounds.start);
    const trailing = text.slice(bounds.end);
    const start = isSurroundingProse(leading, bounds.isDocument) ? bounds.start : 0;
    const end = isSurroundingProse(trailing, bounds.isDocument) ? bounds.end : text.length;
    if (start === 0 && end === text.length) {
        return { html: text, isTrimmed: false };
    }
    return { html: text.slice(start, end), isTrimmed: true };
};
exports.trimToEmailMarkup = trimToEmailMarkup;
const MIME_HTML_PART_HEADER_RE = /^content-type:[ \t]*text\/html\b/im;
const MIME_HEADER_BLOCK_END_RE = /\r?\n[ \t]*\r?\n/;
const MIME_BOUNDARY_RE = /boundary="?[^";\r\n]+/gi;
const MIME_BOUNDARY_PREFIX_RE = /^boundary="?/i;
const QUOTED_PRINTABLE_ATTRIBUTE_RE = /\s[a-z-]+=3D["']/i;
const MAX_MIME_BOUNDARIES = 10;
const QUOTED_PRINTABLE_BYTE_RE = /^=[0-9a-f]{2}$/i;
const BASE64_RE = /^[a-z0-9+/]*={0,2}$/i;
const decodeQuotedPrintable = (text) => {
    const encoder = new TextEncoder();
    return Uint8Array.from(text
        .replace(/=\r?\n/g, "")
        .split(/(=[0-9a-f]{2})/i)
        .flatMap((chunk) => QUOTED_PRINTABLE_BYTE_RE.test(chunk) ? [parseInt(chunk.slice(1), 16)] : Array.from(encoder.encode(chunk))));
};
const decodeBase64 = (text) => {
    const compact = text.replace(/\s/g, "");
    if (!BASE64_RE.test(compact) || compact.length % 4 === 1) {
        return null;
    }
    try {
        return Uint8Array.from(atob(compact), (character) => character.charCodeAt(0));
    }
    catch {
        return null;
    }
};
const createTextDecoder = (charset) => {
    try {
        return new TextDecoder(charset);
    }
    catch {
        return new TextDecoder("utf-8");
    }
};
const readPartHeaders = (text, contentTypeIndex) => {
    const precedingLines = text.slice(0, contentTypeIndex).split(/\r?\n/).slice(0, -1);
    const linesAfterPartStart = [...precedingLines].reverse().findIndex((line) => !line.trim() || line.startsWith("--"));
    const firstHeaderLine = linesAfterPartStart < 0 ? 0 : precedingLines.length - linesAfterPartStart;
    const precedingHeaders = precedingLines.slice(firstHeaderLine).join("\n");
    const fromContentType = text.slice(contentTypeIndex);
    const headerBlockEnd = MIME_HEADER_BLOCK_END_RE.exec(fromContentType);
    if (!headerBlockEnd) {
        return null;
    }
    return {
        headers: `${precedingHeaders}\n${fromContentType.slice(0, headerBlockEnd.index)}`.replace(/\r?\n[ \t]+/g, " "),
        bodyStart: contentTypeIndex + headerBlockEnd.index + headerBlockEnd[0].length,
    };
};
const findPartBodyEnd = (text, bodyStart) => {
    const boundaries = Array.from(new Set((text.match(MIME_BOUNDARY_RE) ?? []).map((match) => match.replace(MIME_BOUNDARY_PREFIX_RE, "").trim()))).slice(0, MAX_MIME_BOUNDARIES);
    const boundaryIndexes = boundaries
        .map((boundary) => text.indexOf(`\n--${boundary}`, bodyStart))
        .filter((index) => index >= 0);
    return boundaryIndexes.length > 0 ? Math.min(...boundaryIndexes) : text.length;
};
const extractMimeHtmlPart = (text) => {
    const contentType = MIME_HTML_PART_HEADER_RE.exec(text);
    if (!contentType) {
        return null;
    }
    const part = readPartHeaders(text, contentType.index);
    if (!part) {
        return null;
    }
    const body = text.slice(part.bodyStart, findPartBodyEnd(text, part.bodyStart)).trim();
    const encoding = /content-transfer-encoding:[ \t]*([\w-]+)/i.exec(part.headers)?.[1]?.toLowerCase();
    const charset = /charset="?([^";\s]+)"?/i.exec(part.headers)?.[1] ?? "utf-8";
    if (encoding === "quoted-printable") {
        return createTextDecoder(charset).decode(decodeQuotedPrintable(body));
    }
    if (encoding === "base64") {
        const bytes = decodeBase64(body);
        return bytes ? createTextDecoder(charset).decode(bytes) : body;
    }
    return body;
};
const normalizePastedEmailSource = (text) => {
    const htmlPart = extractMimeHtmlPart(text);
    if (htmlPart !== null) {
        return htmlPart;
    }
    return QUOTED_PRINTABLE_ATTRIBUTE_RE.test(text) ? new TextDecoder("utf-8").decode(decodeQuotedPrintable(text)) : text;
};
exports.normalizePastedEmailSource = normalizePastedEmailSource;
const getPastedEmailHtmlError = (pasted, html) => {
    if (html.length > emails_1.EMAIL_HTML_IMPORT_MAX_CHARS) {
        return "too_large";
    }
    // A paste that was nothing but scripts strips down to whitespace, which no markup check reports on its own.
    if ((0, exports.isMissingHtmlMarkup)(html) || (pasted.trim().length > 0 && html.trim().length === 0)) {
        return "not_html";
    }
    // A token that survives stripping is escaped text the reader is meant to see. Removing it would rewrite
    // their email, so the paste is refused and they are told which content to edit.
    if ((0, email_1.containsScriptToken)(html)) {
        return "script_tag";
    }
    if ((0, exports.isCanvaEmbedPaste)(html)) {
        return "canva_embed";
    }
    if (html.length > 0 && !(0, exports.hasVisibleEmailContent)(html)) {
        return "no_visible_content";
    }
    return undefined;
};
const preparePastedEmailHtml = (pasted) => {
    const { html: trimmedHtml, isTrimmed } = (0, exports.trimToEmailMarkup)((0, exports.normalizePastedEmailSource)(pasted.trim()));
    const { html, removedCount } = (0, email_1.stripScriptTags)(trimmedHtml.trim());
    return { html, removedScriptTagCount: removedCount, isTrimmed, error: getPastedEmailHtmlError(pasted, html) };
};
exports.preparePastedEmailHtml = preparePastedEmailHtml;
